import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import MetricCard from "../WorkspaceKpi/metrics/MetricCard";
import CompanyMetricCard from "./CompanyMetricCard";
import ConfirmDialog from "./ConfirmDialog";
import PageHeader from "../PageHeader";
import { APPS } from "./constants";

const TOGGLE_PERIOD_NAMES = [
  { key: 0, label: "Yesterday" },
  { key: 1, label: "Today" },
  { key: 2, label: "This Week" },
  { key: 3, label: "This Month" },
];

function Dashboard({
  metrics,
  allMetrics,
  columnCount,
  onUpdateColumns,
  onAddIntegrated,
  onAddManual,
  onRemoveMetric,
  onEditMetric,
  onTriggerAssign,
  onTriggerGamifyForWorkspace,
  onSaveGuideText,
  onSaveDailyGoalText,
  allWorkspaces,
  integrationApps = [],
  isGlobalView = false,
  userRole,
  kpiLoading = false,
  togglePeriod = 3,
  onPeriodChange,
  rawKpis = [],
}) {
  const navigate = useNavigate();
  const [removingMetricId, setRemovingMetricId] = useState(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [workspaceFilter, setWorkspaceFilter] = useState("all");
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);

  // Pending filter state — held inside the popover until the user clicks
  // Apply. Re-syncs to the actual values every time the popover opens so the
  // popover always reflects the current state on entry, and dropping selections
  // mid-edit (close-without-apply) doesn't accidentally change the dashboard.
  const [pendingPeriod, setPendingPeriod] = useState(togglePeriod);
  const [pendingStatus, setPendingStatus] = useState(statusFilter);
  const [pendingWorkspace, setPendingWorkspace] = useState(workspaceFilter);

  useEffect(() => {
    if (isFiltersOpen) {
      setPendingPeriod(togglePeriod);
      setPendingStatus(statusFilter);
      setPendingWorkspace(workspaceFilter);
    }
  }, [isFiltersOpen, togglePeriod, statusFilter, workspaceFilter]);

  const applyFilters = () => {
    if (onPeriodChange && pendingPeriod !== togglePeriod) onPeriodChange(pendingPeriod);
    if (pendingStatus !== statusFilter) setStatusFilter(pendingStatus);
    if (pendingWorkspace !== workspaceFilter) setWorkspaceFilter(pendingWorkspace);
    setIsFiltersOpen(false);
  };

  const resetPendingFilters = () => {
    setPendingPeriod(3);
    setPendingStatus("all");
    setPendingWorkspace("all");
  };

  const isPendingDirty =
    pendingPeriod !== togglePeriod ||
    pendingStatus !== statusFilter ||
    pendingWorkspace !== workspaceFilter;

  const dropdownRef = useRef(null);
  const filtersPopoverRef = useRef(null);
  const viewPopoverRef = useRef(null);
  const isAdmin = userRole === "admin";

  // Count active non-default filters for the "Filters · N" badge.
  // Period default = 3 (Month) per the `togglePeriod = 3` prop default.
  const activeFilterCount =
    (statusFilter !== "all" ? 1 : 0) +
    (workspaceFilter !== "all" ? 1 : 0) +
    (togglePeriod !== 3 ? 1 : 0);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setShowAddDropdown(false);
      if (filtersPopoverRef.current && !filtersPopoverRef.current.contains(event.target)) setIsFiltersOpen(false);
      if (viewPopoverRef.current && !viewPopoverRef.current.contains(event.target)) setIsViewOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredAndSortedMetrics = useMemo(() => {
    let filtered = metrics;

    const query = searchQuery.toLowerCase().trim();
    if (query) {
      filtered = filtered.filter((metric) => {
        const metricName = metric.name.toLowerCase();
        const sourceApp = APPS.find((a) => a.id === metric.appId);
        const appName = sourceApp ? sourceApp.name.toLowerCase() : "";
        return metricName.includes(query) || appName.includes(query);
      });
    }

    if (isGlobalView) {
      filtered = filtered.filter((metric) => {
        const isAssigned = metric.ownerWorkspaceId && metric.ownerWorkspaceId !== "GLOBAL";
        if (statusFilter === "assigned" && !isAssigned) return false;
        if (statusFilter === "unassigned" && isAssigned) return false;
        if (workspaceFilter !== "all") {
          if (metric.ownerWorkspaceId !== workspaceFilter) return false;
        }
        return true;
      });
    }

    return [...filtered].sort((a, b) => {
      const aHasValue = Number(a.currentValue) > 0 ? 1 : 0;
      const bHasValue = Number(b.currentValue) > 0 ? 1 : 0;
      if (aHasValue !== bHasValue) return bHasValue - aHasValue;
      const aIsGamified = a.gameConfig?.status === "live" ? 1 : 0;
      const bIsGamified = b.gameConfig?.status === "live" ? 1 : 0;
      if (aIsGamified !== bIsGamified) return bIsGamified - aIsGamified;
      const aVal = a.deploymentStatus === "active" ? 2 : 1;
      const bVal = b.deploymentStatus === "active" ? 2 : 1;
      return bVal - aVal;
    });
  }, [metrics, allMetrics, searchQuery, statusFilter, workspaceFilter, isGlobalView]);

  // Responsive: ≤1200px → 2 cols, ≤1366px → 3 cols, >1366px → user choice (2/3/4)
  const gridColsClass = { 2: "min-[1367px]:grid-cols-2", 3: "min-[1367px]:grid-cols-3", 4: "min-[1367px]:grid-cols-4" }[columnCount] || "min-[1367px]:grid-cols-3";

  const renderMainContent = () => {
    if (metrics.length === 0) {
      if (kpiLoading) {
        return (
          <div className="mt-4 bg-white border border-gray-100 rounded-2xl py-20 px-6 flex flex-col items-center justify-center text-center shadow-sm">
            <div className="w-16 h-16 bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)] rounded-2xl flex items-center justify-center mb-8 animate-pulse">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7"><path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" /></svg>
            </div>
            <h3 className="text-[18px] leading-[28px] font-semibold text-gray-900">Loading company KPIs</h3>
            <p className="text-gray-400 mt-3 max-w-sm mx-auto text-sm leading-relaxed">Fetching metrics from your workspaces...</p>
          </div>
        );
      }
      return (
        <div className="mt-4 bg-white border border-gray-100 rounded-2xl py-20 px-6 flex flex-col items-center justify-center text-center shadow-sm">
          <div className="w-16 h-16 bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)] rounded-2xl flex items-center justify-center mb-8">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7"><path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" /></svg>
          </div>
          <h3 className="text-[18px] leading-[28px] font-semibold text-gray-900">No metrics found</h3>
          <p className="text-gray-400 mt-3 max-w-sm mx-auto text-sm leading-relaxed">
            {(() => {
              if (isGlobalView) return "Start by creating company-wide metric templates.";
              if (isAdmin) return "Ask your admin to assign metrics or create one directly here.";
              return "No metrics available yet.";
            })()}
          </p>
        </div>
      );
    }
    if (filteredAndSortedMetrics.length === 0) {
      return (
        <div className="mt-4 bg-white border border-gray-100 rounded-2xl py-20 px-6 flex flex-col items-center justify-center text-center shadow-sm">
          <div className="w-16 h-16 bg-gray-50 text-gray-400 rounded-2xl flex items-center justify-center mb-8 border border-gray-100/50">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
          </div>
          <h3 className="text-[18px] leading-[28px] font-semibold text-gray-900">No matching metrics</h3>
          <p className="text-gray-400 mt-3 max-w-sm mx-auto text-sm leading-relaxed">We couldn&apos;t find any metrics matching current filters.</p>
          <button type="button" onClick={() => { setSearchQuery(""); setStatusFilter("all"); setWorkspaceFilter("all"); }} className="mt-8 text-sm font-semibold text-[var(--gmb-primary)] hover:text-[var(--gmb-primary-hover)] transition-all">Clear filters</button>
        </div>
      );
    }
    return (
      <div className={`grid grid-cols-1 max-[1366px]:grid-cols-3 max-[1200px]:grid-cols-2 gap-[16px] ${gridColsClass} dashboard-grid`}>
        {filteredAndSortedMetrics.map((metric) => {
          const rawKpi = Array.isArray(rawKpis) && metric.id ? rawKpis.find((k) => (k._id || k.id) === metric.id) : null;
          const assignments = allMetrics.filter((m) => m.id === metric.id && m.workspaceId !== "GLOBAL");
          let assignedWorkspaces = assignments.map((a) => allWorkspaces.find((ws) => ws.id === a.workspaceId)).filter(Boolean);
          if (isGlobalView && metric._source === "companyKpi" && metric.ownerWorkspaceId && metric.ownerWorkspaceId !== "GLOBAL") {
            const homeWorkspace = allWorkspaces.find((ws) => ws.id === metric.ownerWorkspaceId);
            // Include every workspace this metric is linked to (see
            // GroupKpi.linkedGroupIds / "Assign to workspace(s)"), not just
            // its home — otherwise the catalog card's "Assigned to" pill
            // silently drops workspaces the metric is genuinely live on.
            const linkedIds = (rawKpi?.linkedGroupIds || []).map((id) => id?._id || id);
            const linkedWorkspaces = linkedIds
              .map((id) => allWorkspaces.find((ws) => ws.id === String(id)))
              .filter(Boolean);
            assignedWorkspaces = [homeWorkspace, ...linkedWorkspaces].filter(Boolean);
          }
          const CardComponent = isGlobalView ? CompanyMetricCard : MetricCard;
          // A metric linked onto this board via "Assign to workspace(s)"
          // (GroupKpi.linkedGroupIds) is the SAME document as its home
          // workspace's — editing its appearance from here would silently
          // change it everywhere it's linked, with no visual cue that
          // happened, so appearance editing stays exclusive to Company
          // Metrics; this board only ever shows the metric's own true home
          // (rawKpi.groupId), not the workspace being viewed, when it
          // differs. Gamifying is exempted: a metric assigned to a workspace
          // needs to be gamifiable right there (the save path preserves the
          // real home groupId — see WorkspaceKpi.jsx's homeGroupId), and
          // there was previously no way to gamify it at all short of hunting
          // it down in Company Metrics.
          const rawKpiGroupId = rawKpi?.groupId?._id || rawKpi?.groupId;
          const isLinkedNotOwned = !isGlobalView && !!rawKpiGroupId && String(rawKpiGroupId) !== String(metric.workspaceId);
          return (
            <CardComponent
              key={metric.dashboardId}
              metric={metric}
              isGlobalView={isGlobalView}
              assignedWorkspaces={isGlobalView ? assignedWorkspaces : undefined}
              userRole={userRole}
              allWorkspaces={allWorkspaces}
              integrationApps={integrationApps}
              onRemove={() => setRemovingMetricId(metric.dashboardId)}
              readOnlyLinked={isLinkedNotOwned}
              onEdit={(tab) => {
                if (isLinkedNotOwned && (tab || "appearance") !== "gamification") return;
                onEditMetric(metric, tab || "appearance");
              }}
              onGamify={() => onEditMetric(metric, "gamification")}
              onAssign={() => onTriggerAssign(metric)}
              onGamifyForWorkspace={() => onTriggerGamifyForWorkspace(metric)}
              onSaveGuideText={onSaveGuideText}
              onSaveDailyGoalText={onSaveDailyGoalText}
              allMetrics={allMetrics || []}
              rawKpi={rawKpi}
              rawKpis={rawKpis || []}
              togglePeriod={togglePeriod}
            />
          );
        })}
        {isAdmin && (
          <button type="button" onClick={() => setShowAddDropdown(true)} className="min-h-[220px] w-full border-2 border-dashed border-gray-100 rounded-2xl hidden lg:flex flex-col items-center justify-center hover:border-[var(--gmb-primary-tint)] hover:bg-gray-50/50 transition-all group bg-white">
            <div className="w-10 h-10 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-white group-hover:text-[var(--gmb-primary)] transition-all">+</div>
            <span className="mt-4 text-[14px] leading-[20px] font-semibold text-gray-400 group-hover:text-[var(--gmb-primary)]">Add metric</span>
          </button>
        )}
      </div>
    );
  };

  const pageTitle = isGlobalView ? "Company Metrics" : "Workspace metrics";
  // Match the subheader copy that used to live in the inline title block.
  let pageSubtitle;
  if (isGlobalView) pageSubtitle = "Manage and assign company-wide metric templates.";
  else if (isAdmin) pageSubtitle = "Track and activate gameplay metrics for this workspace.";
  else pageSubtitle = "View active performance metrics for your workspace.";

  return (
    <>
      <PageHeader headerText={pageTitle} subheaderText={pageSubtitle} onBack={() => navigate(-1)} inlineBack />
      {/* Mobile-only: PageHeader's banner has zero bottom margin below md
          (matches Feed/Widgets), so this wrapper supplies its own top gap on
          mobile instead of the content sitting flush against the banner.
          Side padding also follows the same 1rem→1.5rem→(unchanged desktop)
          gutter as Feed/the dashboard, rather than a flat 32px on phones. */}
      <div className="max-w-[1600px] mx-auto pl-[16px] pr-[16px] md:pl-[32px] md:pr-[32px] pt-[16px] md:pt-0 pb-8">
        {isAdmin && (
          <div className="lg:hidden mb-4 rounded-xl border border-[var(--gmb-primary-tint)] bg-[var(--gmb-primary-soft)] px-4 py-3 flex items-start gap-3">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 mt-0.5 flex-shrink-0 text-[var(--gmb-primary)]">
              <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
            </svg>
            <p className="text-[12.5px] leading-[18px] font-medium text-gray-700">
              You're viewing metrics in read-only mode on mobile. Switch to a desktop browser to create, edit, gamify, assign, or remove metrics.
            </p>
          </div>
        )}
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3 xl:gap-6 mb-4 xl:mb-8">
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2">
          {/* Search — primary control, stays visible */}
          <div className="relative w-full md:w-64">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 pl-11 pr-4 bg-white border border-gray-200 rounded-xl text-[14px] leading-[20px] font-medium text-gray-900 placeholder:text-gray-400 focus:border-[var(--gmb-primary)] focus:ring-4 focus:ring-[var(--gmb-primary-soft)] outline-none transition-colors"
            />
          </div>

          {/* Filters button — collapses Period + Assignment + Workspace into one popover */}
          {isAdmin && isGlobalView && (
            <div className="relative" ref={filtersPopoverRef}>
              <button
                type="button"
                onClick={() => setIsFiltersOpen((p) => !p)}
                className="flex items-center gap-2 h-10 px-4 bg-white border border-gray-200 rounded-xl text-[14px] leading-[20px] font-medium text-[#475467] hover:border-gray-300 transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-gray-400">
                  <line x1="21" y1="6" x2="3" y2="6" /><line x1="17" y1="12" x2="3" y2="12" /><line x1="11" y1="18" x2="3" y2="18" />
                  <circle cx="17" cy="6" r="1.6" fill="currentColor" stroke="none" />
                  <circle cx="13" cy="12" r="1.6" fill="currentColor" stroke="none" />
                  <circle cx="7" cy="18" r="1.6" fill="currentColor" stroke="none" />
                </svg>
                Filters
                {activeFilterCount > 0 && (
                  <span className="bg-[var(--gmb-primary)] text-white text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full leading-none min-w-[18px] text-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              {isFiltersOpen && (
                <div className="absolute right-0 top-full mt-2 z-[999] w-[320px] bg-white border border-gray-100 rounded-xl p-4 animate-in fade-in slide-in-from-top-2 duration-150 shadow-[0_12px_32px_-8px_rgba(16,24,40,0.12),0_4px_12px_-4px_rgba(16,24,40,0.08)]">
                  {/* Period */}
                  {onPeriodChange && (
                    <div className="mb-4">
                      <p className="text-[11px] leading-[16px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Period</p>
                      <div className="flex bg-gray-50 border border-gray-200 rounded-lg p-1 gap-1">
                        {TOGGLE_PERIOD_NAMES.map((item) => {
                          const isOn = pendingPeriod === item.key;
                          return (
                            <button
                              key={item.key}
                              type="button"
                              onClick={() => setPendingPeriod(item.key)}
                              className={`flex-1 min-w-0 h-8 px-2 flex items-center justify-center rounded-md text-[11.5px] leading-[16px] font-medium transition-colors ${isOn ? "bg-[var(--gmb-primary)] text-white" : "text-gray-600 hover:bg-white"}`}
                            >
                              {item.label.replace("This ", "")}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Assignment */}
                  <div className="mb-4">
                    <p className="text-[11px] leading-[16px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Assignment</p>
                    <div className="flex bg-gray-50 border border-gray-200 rounded-lg p-1 gap-1">
                      {[{ key: "all", label: "All" }, { key: "assigned", label: "Assigned" }, { key: "unassigned", label: "Unassigned" }].map((item) => {
                        const isOn = pendingStatus === item.key;
                        return (
                          <button
                            key={item.key}
                            type="button"
                            onClick={() => setPendingStatus(item.key)}
                            className={`flex-1 min-w-0 h-8 px-2 flex items-center justify-center rounded-md text-[11.5px] leading-[16px] font-medium transition-colors ${isOn ? "bg-[var(--gmb-primary)] text-white" : "text-gray-600 hover:bg-white"}`}
                          >
                            {item.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Workspace */}
                  <div className="mb-4">
                    <p className="text-[11px] leading-[16px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Workspace</p>
                    <div className="max-h-[180px] overflow-y-auto flex flex-col gap-1">
                      {[{ id: "all", name: "All workspaces" }, ...allWorkspaces].map((ws) => {
                        const isOn = pendingWorkspace === ws.id;
                        return (
                          <button
                            key={ws.id}
                            type="button"
                            onClick={() => setPendingWorkspace(ws.id)}
                            className={`h-8 px-3 text-left rounded-lg text-[12px] leading-[18px] font-medium transition-colors ${isOn ? "bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)]" : "text-gray-600 hover:bg-gray-50"}`}
                          >
                            {ws.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Footer — Reset + Apply */}
                  <div className="flex items-center justify-between gap-2 pt-3 mt-1 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={resetPendingFilters}
                      className="h-9 px-3 text-[12px] leading-[18px] font-medium text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      onClick={applyFilters}
                      disabled={!isPendingDirty}
                      className="h-9 px-5 rounded-lg bg-[var(--gmb-primary)] text-white text-[12px] leading-[18px] font-semibold hover:bg-[var(--gmb-primary-hover)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* View button — collapses Columns picker into a popover.
              Column count only ever takes effect at >=1367px (see gridColsClass
              below); at narrower widths the grid falls back to fixed breakpoint
              columns, so the control has nothing to do below desktop width and
              is hidden there. */}
          {isAdmin && (
            <div className="relative hidden md:block" ref={viewPopoverRef}>
              <button
                type="button"
                onClick={() => setIsViewOpen((p) => !p)}
                className="flex items-center justify-center w-10 h-10 bg-white border border-gray-200 rounded-xl text-gray-600 hover:border-gray-300 hover:text-gray-800 transition-colors"
                aria-label="View options"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-[18px] h-[18px]" aria-hidden="true">
                  <rect width="7" height="7" x="3" y="3" rx="1" />
                  <rect width="7" height="7" x="14" y="3" rx="1" />
                  <rect width="7" height="7" x="14" y="14" rx="1" />
                  <rect width="7" height="7" x="3" y="14" rx="1" />
                </svg>
              </button>
              {isViewOpen && (
                <div className="absolute right-0 top-full mt-2 z-[999] w-[240px] bg-white border border-gray-100 rounded-xl p-4 animate-in fade-in slide-in-from-top-2 duration-150 shadow-[0_12px_32px_-8px_rgba(16,24,40,0.12),0_4px_12px_-4px_rgba(16,24,40,0.08)]">
                  <p className="text-[11px] leading-[16px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Columns</p>
                  <div className="flex gap-2">
                    {[2, 3, 4].map((n) => {
                      const isOn = columnCount === n;
                      const widths = { 2: [8, 14], 3: [5.5, 9.25, 16.5], 4: [2, 7.5, 13, 18.5] };
                      const w = { 2: 8, 3: 5.5, 4: 4 }[n];
                      return (
                        <button
                          key={n}
                          type="button"
                          onClick={() => onUpdateColumns(n)}
                          className={`flex-1 h-12 rounded-lg border-2 flex items-center justify-center transition-colors ${isOn ? "border-[var(--gmb-primary)] bg-[var(--gmb-primary-soft)]" : "border-gray-200 bg-white hover:border-gray-300"}`}
                          aria-label={`${n} columns`}
                        >
                          <svg viewBox="0 0 24 14" width="28" height="16" aria-hidden="true">
                            {widths[n].map((x) => (
                              <rect key={x} x={x} y="2" width={w} height="10" rx="1" fill={isOn ? "var(--gmb-primary)" : "#98A2B3"} />
                            ))}
                          </svg>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          </div>

          {/* Create metric — primary CTA, pinned right. Desktop-only: the creation
              flows (app selector / manual metric builder) aren't built for mobile,
              so this stays hidden below lg and the banner above explains why. */}
          {isAdmin && (
            <div className="relative hidden lg:block" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setShowAddDropdown(!showAddDropdown)}
                className="bg-[var(--gmb-primary)] hover:bg-[var(--gmb-primary-hover)] text-white h-10 px-5 rounded-xl font-semibold text-[14px] leading-[20px] shadow-sm transition-colors flex items-center justify-center whitespace-nowrap"
              >
                <span className="mr-2 text-lg leading-none">+</span>
                Create metric
              </button>
              {showAddDropdown && (
                <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-100 rounded-2xl py-2 z-50 animate-in fade-in slide-in-from-top-2 shadow-[0_12px_32px_-8px_rgba(16,24,40,0.12),0_4px_12px_-4px_rgba(16,24,40,0.08)]">
                  <button type="button" onClick={() => { onAddIntegrated(); setShowAddDropdown(false); }} className="w-full px-5 py-4 text-left hover:bg-gray-50 transition-colors group">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)] flex items-center justify-center shrink-0">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>
                      </div>
                      <div>
                        <p className="text-[14px] leading-[20px] font-semibold text-gray-900">Integrated metric</p>
                        <p className="text-[12px] leading-[18px] text-gray-500 font-normal">Powered by connected apps.</p>
                      </div>
                    </div>
                  </button>
                  <div className="h-px bg-gray-100 mx-4 my-1" />
                  <button type="button" onClick={() => { onAddManual(); setShowAddDropdown(false); }} className="w-full px-5 py-4 text-left hover:bg-gray-50 transition-colors group">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)] flex items-center justify-center shrink-0">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                      </div>
                      <div>
                        <p className="text-[14px] leading-[20px] font-semibold text-gray-900">Manual metric</p>
                        <p className="text-[12px] leading-[18px] text-gray-500 font-normal">Manually updated performance data.</p>
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          )}
      </div>

      {renderMainContent()}

      {/* The grid's Tailwind max-width variants only ever go down to 2
          columns (the "≤1200px → 2 cols" comment above is tablet/laptop
          sizing) — on an actual phone (≤639px) that squeezes each card to
          ~170px, which isn't enough room for the icon + title + badges,
          causing the LIVE/trophy badges to crowd and visually overlap.
          Force a single column below 640px; everything ≥640px is untouched. */}
      <style>{`
        @media (max-width: 639px) {
          .dashboard-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {removingMetricId && (
        <ConfirmDialog
          variant="danger"
          title="Remove metric?"
          message="This removes the metric from this view. You can add it back anytime."
          confirmText="Remove metric"
          onConfirm={() => { onRemoveMetric(removingMetricId); setRemovingMetricId(null); }}
          onCancel={() => setRemovingMetricId(null)}
        />
      )}
    </div>
    </>
  );
}

export default Dashboard;
